/**
 * The colour behind a person's initials — card #53.
 *
 * "Users should be able to specify a colour or a photo like my one in their
 * settings." This is the colour half. Every avatar without a photo used to be
 * the same flat `bg-secondary` grey, so a column of six unassigned-looking
 * faces told you nothing until you read six sets of initials.
 *
 * **Names, not hex.** A stored `#0d9488` is one colour, and an avatar has to
 * work on a white page and a near-black one. The database stores `teal` and
 * each theme decides what teal is, the same way §6.6's statuses store a colour
 * chosen by an admin and every chip renders it through one component.
 *
 * **A choice, with a good default.** `null` is not "grey" — it is "whatever
 * this person's id hashes to", so everybody is distinguishable from the moment
 * they are created and the setting is a correction rather than a chore. Which
 * is also why the palette is fixed: a free colour picker would let two people
 * pick indistinguishable blues and let one pick something unreadable.
 */
export const AVATAR_COLOURS = [
    'slate',
    'red',
    'orange',
    'amber',
    'green',
    'teal',
    'sky',
    'blue',
    'violet',
    'fuchsia',
    'pink',
    'rose',
];
/** Human names for the picker, so the radio group reads as words not slugs. */
export const AVATAR_COLOUR_LABELS = {
    slate: 'Slate',
    red: 'Red',
    orange: 'Orange',
    amber: 'Amber',
    green: 'Green',
    teal: 'Teal',
    sky: 'Sky',
    blue: 'Blue',
    violet: 'Violet',
    fuchsia: 'Fuchsia',
    pink: 'Pink',
    rose: 'Rose',
};
export function isAvatarColour(value) {
    return typeof value === 'string' && AVATAR_COLOURS.includes(value);
}
/**
 * The colour a person gets when they have not chosen one.
 *
 * Hashed from the id rather than assigned in creation order, so it does not
 * change when someone else is deactivated, and it is the same on every screen
 * without anything having to be stored. FNV-1a: small, dependency-free, and
 * well enough distributed for twelve buckets.
 */
export function defaultAvatarColour(userId) {
    let hash = 0x811c9dc5;
    for (let index = 0; index < userId.length; index += 1) {
        hash ^= userId.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return AVATAR_COLOURS[hash % AVATAR_COLOURS.length];
}
/** The stored choice, or the default for this person. Never null. */
export function avatarColourFor(userId, chosen) {
    return isAvatarColour(chosen) ? chosen : defaultAvatarColour(userId);
}
//# sourceMappingURL=avatar-colour.js.map